import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {ExpandableIcon} from '@/web-components';
import {BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../../constants';
import {isBillableSplitAllowed} from '../../../../shared/util/cache/TimeRegistrationSettingsUtil';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';
import {isClientUser, showRevenue as canViewRevenue, showCost as canViewCost} from '../../../../shared/util/PermissionsUtil';
import ProjectUtil from '../../../../shared/util/ProjectUtil';
import {Element, GridContainer, Header, IconWrapper, Row, TotalsBox} from './ProjectScopingTotals_styled';
import {useProjectTotals} from './ProjectScopingTotalsContext';
import {AvailabilityTile} from './totals-header-box-tiles/AvailabilityTile';
import {DatesAndRevenueTile} from './totals-header-box-tiles/DatesAndRevenueTile';
import {EstimateAndTimeTile} from './totals-header-box-tiles/EstimateAndTimeTile';
import {RemainingAndBaselineTile} from './totals-header-box-tiles/RemainingAndBaselineTile';
import {TileVariant} from './totals-header-box-tiles/TotalsTypes';
import {FixedPriceLeftTile} from './totals-header-box-tiles/FixedPriceLeftTile';
import {FixedPriceMiddleTile} from './totals-header-box-tiles/FixedPriceMiddleTile';
import {FixedPriceRightTile} from './totals-header-box-tiles/FixedPriceRightTile';
import {Tile} from './totals-header-box-tiles/Tile_styled';
import {ProjectScopingTotalsRows as TotalsRows} from './ProjectScopingTotalsRows';
import {HorizontalBar} from './ProjectScopingTotals_styled';

const HeaderTotalsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
	grid-gap: 24px 48px;
	padding-right: 36px;
`;

// Responsive grid specifically for T&M, Fixed Hours and T&M retainers
const TMRHeaderTotalsGrid = styled.div`
	display: grid;

	/* 2x2 grid layout for viewport < 1443px */
	grid-template-columns: repeat(2, minmax(250px, 1fr));
	grid-gap: 28px 48px;

	/* Single row layout (4 columns) for viewport >= 1443px */
	@media (min-width: 1443px) {
		grid-template-columns: repeat(4, minmax(180px, 1fr));
		grid-gap: 24px 48px;
	}
`;

// Attempt to fit the availability tile into the secondary row of one of the other tiles instead of showing it as its own standalone tile if there is space for it.
const getAvailabilityTile = (
	showRevenue: boolean,
	showBillableSplit: boolean,
	showBaseline: boolean,
	showAvailabilityOnScoping: boolean
): TileVariant | null => {
	if (!showAvailabilityOnScoping) return null;

	// If none of the other secondary rows are shown, show as standalone column
	if (!showRevenue && !showBillableSplit && !showBaseline) return TileVariant.Availability;

	if (!showRevenue) return TileVariant.DatesAndRevenue;

	if (!showBillableSplit) return TileVariant.EstimateAndTime;

	if (!showBaseline) return TileVariant.RemainingAndBaseline;

	return TileVariant.Availability;
};

/**
 * Create the project scoping header section in a big box (Expanded)
 *
 * @returns JSX Project Scoping Totals Box Component
 */
export const ProjectScopingTotalsBox = (): JSX.Element => {
	const {totalsExpanded, toggleTotalsExpanded, totalsData, settings, projectData} = useProjectTotals();
	const {formatMessage} = useIntl();

	const hasFinancialServiceInScoping = hasFeatureFlag('use_financial_service_in_scoping');
	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	const projectTracksRevenue = ProjectUtil.projectTracksRevenue(projectData);
	const isNonBillable = !projectTracksRevenue;

	const isFixedPriceBudgetType = projectData.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	const isFixedPriceV2 =
		projectTracksRevenue &&
		(isFixedPriceBudgetType ||
			(projectData.budgetType === BUDGET_TYPE.RETAINER &&
				projectData.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE));

	const showBillableSplit = !isNonBillable && isBillableSplitAllowed();
	const showBaseline = !!settings.isBaselineProject;
	const showAvailabilityOnScoping = !!settings.isUsingProjectAllocation && !isClientUser();

	const showRevenue = projectTracksRevenue && canViewRevenue();
	const showCost = canViewCost();

	const availabilityTile = getAvailabilityTile(showRevenue, showBillableSplit, showBaseline, showAvailabilityOnScoping);
	const showAvailabilityTile = availabilityTile === TileVariant.DatesAndRevenue;

	const [isNarrowViewport, setIsNarrowViewport] = useState(window.innerWidth <= 1600);

	const useShortTextComponents =
		hasFinancialServiceInScoping && hasFinancialCategoriesUpdate && !isFixedPriceV2 && !isNonBillable;

	useEffect(() => {
		const handleResize = () => {
			setIsNarrowViewport(window.innerWidth <= 1443);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return (
		<GridContainer data-userpilot="project-totals-header">
			<TotalsBox key="dates-revenue-box" data-cy="project-totals-header">
				{!useShortTextComponents && (
					<Row bottomMargin={16}>
						<Element split>
							<Header>Project totals</Header>
						</Element>
						<Element alignRight>
							<IconWrapper>
								<ExpandableIcon
									isOpen={totalsExpanded}
									onClick={toggleTotalsExpanded}
									cy={'collapse-project-totals'}
								/>
							</IconWrapper>
						</Element>
					</Row>
				)}
				{useShortTextComponents ? (
					<TMRHeaderTotalsGrid>
						{/* T&M, Fixed Hours and T&M retainers */}
						<Tile>
							<Header bold>{formatMessage({id: 'common.hours'})}</Header>
							<HorizontalBar transparent />
							<TotalsRows.PlannedHoursShortText />
							<TotalsRows.ActualHoursShortText />
							<HorizontalBar />
							<TotalsRows.RemainingWorkHoursShortText />
							<TotalsRows.ProjectedTotalHoursShortText />
						</Tile>
						<Tile>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									marginBottom: 0,
								}}
							>
								{totalsData.isEstimatedInHours ? (
									<Header bold>{formatMessage({id: 'project_scoping.billable_recovery'})}</Header>
								) : (
									<Header bold>{formatMessage({id: 'sync_jira_project_modal.radio.story_points'})}</Header>
								)}
								{isNarrowViewport && (
									<IconWrapper customMarginRight="-10px">
										<ExpandableIcon
											isOpen={totalsExpanded}
											onClick={toggleTotalsExpanded}
											cy={'collapse-project-totals'}
										/>
									</IconWrapper>
								)}
							</div>
							<HorizontalBar transparent />
							{totalsData.isEstimatedInHours ? (
								<>
									<TotalsRows.ActualNonBillableHoursShortText />
									<TotalsRows.ActualHoursPercentageBillable />
									<HorizontalBar />
									<TotalsRows.ProjectedTotalNonBillableShortText />
									<TotalsRows.ProjectedTotalHoursPercentageBillable />
								</>
							) : (
								<>
									<TotalsRows.EstimatedPointsShortText />
									<TotalsRows.CompletedPointsShortText />
									<HorizontalBar />
									<TotalsRows.RemainingPointsShortText />
								</>
							)}
						</Tile>
						{showRevenue && (
							<>
								<Tile>
									<Header bold>
										{formatMessage({
											id: projectData.useBaseline
												? 'project_scoping.baseline_financials'
												: 'project_scoping.estimated_financials',
										})}
									</Header>
									<HorizontalBar transparent />
									<TotalsRows.PlannedRevenueShortText />
									{showCost && <TotalsRows.PlannedCostShortText />}
									<HorizontalBar />

									{showCost && <TotalsRows.PlannedProfitShortText />}
									{showCost && <TotalsRows.PlannedMarginShortText />}
								</Tile>
								<Tile>
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											marginBottom: 0,
										}}
									>
										<Header bold>
											{formatMessage({id: 'project_scoping.projected_total_financials'})}
										</Header>
										{!isNarrowViewport && (
											<IconWrapper customMarginRight="-10px">
												<ExpandableIcon
													isOpen={totalsExpanded}
													onClick={toggleTotalsExpanded}
													cy={'collapse-project-totals'}
												/>
											</IconWrapper>
										)}
									</div>

									<HorizontalBar transparent />

									<TotalsRows.ProjectedTotalRevenueShortText />
									{showCost && <TotalsRows.ProjectedTotalCostShortText />}
									<HorizontalBar />

									{showCost && <TotalsRows.ProjectedTotalProfitShortText />}
									{showCost && <TotalsRows.ProjectedTotalMarginShortText />}
								</Tile>
							</>
						)}
					</TMRHeaderTotalsGrid>
				) : (
					<HeaderTotalsGrid>
						{hasFinancialServiceInScoping && hasFinancialCategoriesUpdate ? (
							isFixedPriceV2 ? (
								<>
									<FixedPriceLeftTile />
									<FixedPriceMiddleTile />
									<FixedPriceRightTile />
								</>
							) : isNonBillable ? (
								totalsData.isEstimatedInHours ? (
									<>
										<Tile>
											<TotalsRows.PlannedHours />
											<TotalsRows.ActualHours />
										</Tile>
										<Tile>
											<TotalsRows.RemainingWorkHours />
											<TotalsRows.ProjectedTotalHours />
										</Tile>
										{showCost && (
											<Tile>
												<TotalsRows.PlannedCost />
												<TotalsRows.ActualCost />
											</Tile>
										)}
										<Tile>
											{showCost && <TotalsRows.ProjectedTotalCost />}
											<TotalsRows.Progress />
										</Tile>
									</>
								) : (
									<>
										<Tile>
											<TotalsRows.ActualHours />
											<TotalsRows.PlannedHours />
											<TotalsRows.RemainingWorkHours />
											<TotalsRows.ProjectedTotalHours />
										</Tile>
										<Tile>
											<TotalsRows.Progress />
											<TotalsRows.EstimatedPoints />
											<TotalsRows.CompletedPoints />
											<TotalsRows.RemainingPoints />
										</Tile>
										{showCost && (
											<Tile>
												<TotalsRows.PlannedCost />
												<TotalsRows.ActualCost />
												<TotalsRows.RemainingCost />
												<TotalsRows.ProjectedTotalCost />
											</Tile>
										)}
									</>
								)
							) : null
						) : (
							<>
								<DatesAndRevenueTile
									showRevenue={showRevenue}
									isFixedPriceV2={isFixedPriceV2}
									hasFinancialServiceInScoping={hasFinancialServiceInScoping}
									showAvailabilityTile={showAvailabilityTile}
								/>
								<EstimateAndTimeTile
									showBillableSplit={showBillableSplit}
									showAvailabilityTile={showAvailabilityTile}
								/>
								<RemainingAndBaselineTile
									showRevenue={showRevenue}
									showBaseline={showBaseline}
									showAvailabilityTile={showAvailabilityTile}
								/>
								{availabilityTile === TileVariant.Availability && <AvailabilityTile />}
							</>
						)}
					</HeaderTotalsGrid>
				)}
			</TotalsBox>
		</GridContainer>
	);
};
